import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../apiService';
import type {
  McpUsageOrg,
  McpUsagePerson,
  ModuleRegistry,
  OrganizationPatch,
  OrganizationSummary,
  OrganizationUsagePatch,
  UsageSettings,
} from '../../apiService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useOptionalAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/formatDateTime';
import { isMcpOnlyModuleMap } from '../../utils/moduleAccess';
import { formatNumber } from '../../utils/plural';

// BAR-135 §5.5: Organizations tab. Lists org docs with their computed effective
// per-module entitlements and lets an admin toggle modules per org. The write
// gate is the interim one in auth_service (`is_admin` and a BARK internal
// company claim); listing other tenants' orgs needs super_admin. Each toggle
// routes through a confirm dialog (billing-tied surface) and shows the "~60s
// propagation" notice — the resolver's TTL cache means changes apply within
// ≤60s with no redeploy.
//
// BAR-1528: each org shows its plan, its MCP usage and its people. Free
// organizations (one per self-serve MCP signup: a person, or an email domain)
// are left out unless "Show free organizations" is on, because they would flood
// the list. The plan is switched here (it moved from the analytics dashboard's
// "MCP customers" tab); auth_service allows it for super_admins only and
// audits it as `update_org_plan`. Call analytics comes from the dashboard's
// backend; allowance and billing totals come from auth_service.

interface OrganizationsPanelProps {
  authToken: string;
}

// Human labels for the known module ids (registry vocabulary, apiService HomeAgentAction).
const MODULE_LABELS: Record<string, string> = {
  regulatory_research: 'Regulatory Research',
  build_artifact: 'Build',
  quick_lookup: 'Quick Lookup',
  project_library: 'Project Library',
  reporting: 'Reporting',
  classification: 'Classification',
  // BAR-962: this org may connect Claude/Codex to BARK over MCP. Unlike the
  // others it gates no screen in this app — only the MCP gateway reads it.
  mcp_access: 'BARK over MCP',
};

// Fallback ordering/vocabulary if a doc omits the computed map for some reason.
const KNOWN_MODULE_IDS = [
  'regulatory_research',
  'build_artifact',
  'quick_lookup',
  'project_library',
  'reporting',
  'classification',
  'mcp_access',
];

function moduleLabel(id: string): string {
  return MODULE_LABELS[id] || id.replace(/_/g, ' ');
}

// A missing plan reads as full: every org doc written before BAR-1528 has none.
function planOf(org: OrganizationSummary): 'free' | 'full' {
  return org.plan === 'free' ? 'free' : 'full';
}

// The free-signup namespace (`shared.authz.core.FREE_SIGNUP_ORG_ID_RE`): a
// personal `p-` org or a domain `d-` org. The namespace is reserved: only free
// signup creates ids in it, and admin create and the paused Stripe trial refuse
// them, so a customer organization cannot sit in it. Only these are offered
// "Back to free": a customer organization on the free plan would use the free
// default allowance, which is never what switching a customer means.
const FREE_SIGNUP_ORG_ID = /^(?:p-[0-9a-f]{16}|d-[a-z0-9][a-z0-9-]*)$/;

function inFreeNamespace(orgId: string): boolean {
  return FREE_SIGNUP_ORG_ID.test(orgId);
}

type UsagePeriod = '7d' | '30d';
type AllowanceMode = 'default' | 'unlimited' | 'amount';

const PERIOD_LABELS: Record<UsagePeriod, string> = { '7d': '7 days', '30d': '30 days' };
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value: number): string {
  return USD.format(value);
}

function allowanceModeOf(org: OrganizationSummary): AllowanceMode {
  if (org.usage?.allowance_source === 'plan_default') return 'default';
  return org.usage?.allowance_tokens == null ? 'unlimited' : 'amount';
}

// `shared.authz.usage_terms`: a token of allowance is priced at the Opus input
// price, and the markup is billed on top.
const TOKEN_PRICE_USD = 5.5 / 1_000_000;
const DEFAULT_MARKUP_PCT = 20;
const MAX_ALLOWANCE_TOKENS = 100_000_000_000;

function billedUsdForTokens(tokens: number, markupPct: number): number {
  return tokens * TOKEN_PRICE_USD * (1 + markupPct / 100);
}

function validTokenCount(raw: string): boolean {
  const value = Number(raw);
  return raw.trim() !== '' && Number.isInteger(value) && value >= 0 && value <= MAX_ALLOWANCE_TOKENS;
}

function compactTokens(value: number): string {
  const rounded = Math.round(value);
  if (rounded >= 1_000_000) return `${formatNumber(Number((rounded / 1_000_000).toFixed(1)))}m`;
  if (rounded >= 1_000) return `${formatNumber(Number((rounded / 1_000).toFixed(1)))}k`;
  return formatNumber(rounded);
}

// U-06: dates go through the shared formatter. It answers '—' for a bad value.
function formatWhen(ts: string | null | undefined): string {
  return ts ? formatDateTime(ts) : '—';
}

function formatDay(ts: string | null | undefined): string {
  return ts ? formatDateTime(ts, { context: 'date' }) : '—';
}

/** The MCP clients `GET /api/mcp/people` names, as this panel shows them. */
const MCP_CLIENT_LABELS: Record<string, string> = {
  claude_ai: 'Claude app',
  claude_code: 'Claude Code',
  claude: 'Claude',
  codex: 'Codex',
  grok: 'Grok',
  copilot_studio: 'Copilot Studio',
  microsoft_teams: 'Teams',
  test_client: 'Tests',
  other: 'Other',
  not_recorded: 'Not recorded',
};

/** "Claude app 52 · Codex 18", most calls first (the API's order). A name this
 *  panel does not know yet shows as it came; an own key only, so a name such as
 *  "constructor" is not read off the prototype. */
function formatMcpClients(clients: { client: string; calls: number }[] | undefined): string {
  if (!clients || clients.length === 0) return '—';
  return clients
    .map(({ client, calls }) => {
      const label = Object.prototype.hasOwnProperty.call(MCP_CLIENT_LABELS, client)
        ? MCP_CLIENT_LABELS[client]
        : client;
      return `${label} ${formatNumber(calls)}`;
    })
    .join(' · ');
}

interface PendingPlan {
  org: OrganizationSummary;
  next: 'free' | 'full';
}

interface PeopleView {
  orgId: string;
  state: 'loading' | 'ok' | 'error';
  rows: McpUsagePerson[];
  revealed: boolean;
}

interface PendingToggle {
  org: OrganizationSummary;
  moduleId: string;
  next: boolean;
}

interface UsageTermsDialogProps {
  authToken: string;
  org: OrganizationSummary;
  onCancel: () => void;
  onSaved: (updated: OrganizationSummary) => void;
}

function UsageTermsDialog({ authToken, org, onCancel, onSaved }: UsageTermsDialogProps) {
  const initialMode = allowanceModeOf(org);
  const initialAllowance = org.usage?.allowance_tokens ?? null;
  const initialMarkup = org.usage?.markup_pct ?? 0;
  const initialToolCallPrice = org.usage?.tool_call_usd ?? 0;
  const [mode, setMode] = useState<AllowanceMode>(initialMode);
  const [allowance, setAllowance] = useState(initialAllowance == null ? '' : String(initialAllowance));
  const [markup, setMarkup] = useState(String(initialMarkup));
  const [toolCallPrice, setToolCallPrice] = useState(String(initialToolCallPrice));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowanceNumber = Number(allowance);
  const markupNumber = Number(markup);
  const helperTokens = mode === 'amount'
    ? (allowance.trim() === '' ? null : allowanceNumber)
    : mode === 'default'
      ? initialAllowance
      : null;
  const billedEquivalent = helperTokens != null
    && markup.trim() !== ''
    && Number.isFinite(helperTokens)
    && Number.isFinite(markupNumber)
    && markupNumber >= 0
    ? billedUsdForTokens(helperTokens, markupNumber)
    : null;

  const save = async () => {
    const patch: OrganizationUsagePatch = {};
    if (mode !== initialMode) {
      if (mode === 'default') patch.allowance_default = true;
      else if (mode === 'unlimited') patch.allowance_tokens = null;
    }
    if (mode === 'amount' && (mode !== initialMode || allowanceNumber !== initialAllowance)) {
      if (!validTokenCount(allowance)) {
        setError(`Allowance must be a whole number of tokens from 0 to ${formatNumber(MAX_ALLOWANCE_TOKENS)}.`);
        return;
      }
      patch.allowance_tokens = allowanceNumber;
    }
    if (markup.trim() === '' || !Number.isFinite(markupNumber) || markupNumber < 0 || markupNumber > 1000) {
      setError('Markup must be between 0 and 1000%.');
      return;
    }
    const toolCallPriceNumber = Number(toolCallPrice);
    if (toolCallPrice.trim() === '' || !Number.isFinite(toolCallPriceNumber) || toolCallPriceNumber < 0 || toolCallPriceNumber > 100) {
      setError('Price per tool call must be between 0 and 100 USD.');
      return;
    }
    if (markupNumber !== initialMarkup) patch.markup_pct = markupNumber;
    if (toolCallPriceNumber !== initialToolCallPrice) patch.tool_call_usd = toolCallPriceNumber;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await api.patchOrganization(authToken, org.org_id, { usage: patch });
      onSaved(updated);
    } catch (e: any) {
      setError(e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open
      title={`Usage terms for ${org.display_name || org.company_claim}`}
      description={(
        <span className="block">
          {org.usage && (
            <span className="mb-4 block text-fg">
              <span className="block">
                Used {compactTokens(org.usage.used_tokens)} tokens · Remaining{' '}
                {org.usage.remaining_tokens == null ? 'Unlimited' : `${compactTokens(org.usage.remaining_tokens)} tokens`}
              </span>
              <span className="mt-1 block text-fg-secondary">
                Our cost {formatUsd(org.usage.cost_usd)} · Billed {formatUsd(org.usage.billed_usd)} ·{' '}
                {formatNumber(org.usage.llm_calls)} LLM calls · {formatNumber(org.usage.tool_calls)} tool calls
              </span>
            </span>
          )}
          <span role="radiogroup" aria-label="Allowance mode" className="grid gap-2">
            <label className="flex items-center gap-2 text-fg">
              <input type="radio" name={`allowance-${org.org_id}`} checked={mode === 'default'} onChange={() => setMode('default')} />
              Plan default
            </label>
            <label className="flex items-center gap-2 text-fg">
              <input type="radio" name={`allowance-${org.org_id}`} checked={mode === 'unlimited'} onChange={() => setMode('unlimited')} />
              Unlimited
            </label>
            <label className="flex items-center gap-2 text-fg">
              <input type="radio" name={`allowance-${org.org_id}`} checked={mode === 'amount'} onChange={() => setMode('amount')} />
              Set amount
            </label>
          </span>
          {mode === 'amount' && (
            <label className="mt-3 flex flex-col gap-1 text-fg-secondary">
              Allowance in tokens
              <input
                type="number"
                min="0"
                max={MAX_ALLOWANCE_TOKENS}
                step="1"
                value={allowance}
                onChange={(e) => setAllowance(e.target.value)}
                className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
              />
            </label>
          )}
          <span className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-fg-secondary">
              Markup %
              <input
                type="number"
                min="0"
                max="1000"
                step="any"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
              />
            </label>
            <label className="flex flex-col gap-1 text-fg-secondary">
              Price per tool call USD
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={toolCallPrice}
                onChange={(e) => setToolCallPrice(e.target.value)}
                className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
              />
            </label>
          </span>
          {billedEquivalent != null && (
            <span className="mt-3 block text-fg-secondary">
              {compactTokens(helperTokens ?? 0)} tokens ≈ {formatUsd(billedEquivalent)} billed at this markup.
            </span>
          )}
          <span className="mt-3 block text-fg-tertiary">
            A token is priced at the Opus input price, $5.50 per million, before markup. The allowance is a total and never resets by itself.
          </span>
        </span>
      )}
      error={error || undefined}
      confirmLabel="Save"
      submitting={submitting}
      onConfirm={save}
      onCancel={() => {
        if (!submitting) onCancel();
      }}
    />
  );
}

interface FreePlanAllowanceProps {
  authToken: string;
  onSaved: () => void;
}

// The free plan's default allowance: what every free organization without its
// own allowance may use. super_admin only (auth_service refuses the rest).
function FreePlanAllowance({ authToken, onSaved }: FreePlanAllowanceProps) {
  const [settings, setSettings] = useState<UsageSettings | null>(null);
  const [value, setValue] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getUsageSettings(authToken)
      .then((loaded) => {
        if (cancelled) return;
        setSettings(loaded);
        setValue(String(loaded.free_allowance_tokens));
      })
      .catch((e: any) => {
        if (!cancelled) setLoadError(e?.message || 'Could not load the free plan allowance');
      });
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  const save = async () => {
    if (!validTokenCount(value)) {
      setSaveError(`Allowance must be a whole number of tokens from 0 to ${formatNumber(MAX_ALLOWANCE_TOKENS)}.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await api.patchUsageSettings(authToken, Number(value));
      setSettings(updated);
      setValue(String(updated.free_allowance_tokens));
      setSaved(true);
      onSaved();
    } catch (e: any) {
      setSaveError(e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const tokens = Number(value);
  const unchanged = settings != null && value.trim() !== '' && tokens === settings.free_allowance_tokens;

  return (
    <section aria-label="Free plan allowance" className="mb-6 rounded-md border border-border-default bg-surface p-4">
      <h3 className="text-sm font-semibold text-fg">Free plan allowance</h3>
      <p className="mt-1 text-sm text-fg-secondary">
        Every free organization without its own allowance may use this many tokens in total. Changes apply within 2 minutes.
      </p>
      {loadError ? (
        <p role="alert" className="mt-3 text-sm text-danger">{loadError}</p>
      ) : settings == null ? (
        <p className="mt-3 text-sm text-fg-secondary">Loading…</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm text-fg-secondary">
            Tokens
            <input
              type="number"
              min="0"
              max={MAX_ALLOWANCE_TOKENS}
              step="1"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setSaved(false);
              }}
              className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
            />
          </label>
          <button
            type="button"
            onClick={save}
            disabled={saving || unchanged}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <span className="text-sm text-fg-secondary">
            {validTokenCount(value) && `≈ ${formatUsd(billedUsdForTokens(tokens, DEFAULT_MARKUP_PCT))} billed at the default 20% markup`}
            {settings.source === 'default' && ' · not set yet, the code default applies'}
            {saved && ' · Saved'}
          </span>
        </div>
      )}
      {saveError && <p role="alert" className="mt-2 text-sm text-danger">{saveError}</p>}
    </section>
  );
}

interface EditOrganizationDialogProps {
  authToken: string;
  org: OrganizationSummary;
  onCancel: () => void;
  onSaved: (updated: OrganizationSummary) => void;
}

function parseDomains(raw: string): string[] {
  const domains = raw
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(domains));
}

// The org doc fields that are not access or money: the name people see, the
// descriptive SSO domains, and active / suspended. The company claim is the
// tenant key (storage paths, every user's company), so it is never edited.
function EditOrganizationDialog({ authToken, org, onCancel, onSaved }: EditOrganizationDialogProps) {
  const [displayName, setDisplayName] = useState(org.display_name || '');
  const [domains, setDomains] = useState((org.sso_domains || []).join(', '));
  const [orgStatus, setOrgStatus] = useState<'active' | 'suspended'>(org.status === 'suspended' ? 'suspended' : 'active');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const patch: OrganizationPatch = {};
    const name = displayName.trim();
    if (name !== (org.display_name || '')) patch.display_name = name;
    const nextDomains = parseDomains(domains);
    if (nextDomains.join(',') !== (org.sso_domains || []).join(',')) patch.sso_domains = nextDomains;
    if (orgStatus !== (org.status === 'suspended' ? 'suspended' : 'active')) patch.status = orgStatus;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onSaved(await api.patchOrganization(authToken, org.org_id, patch));
    } catch (e: any) {
      setError(e?.message || 'Update failed');
      setSubmitting(false);
    }
  };

  const suspending = orgStatus === 'suspended' && org.status !== 'suspended';

  return (
    <ConfirmDialog
      open
      title={`Edit ${org.display_name || org.company_claim}`}
      description={(
        <span className="grid gap-3">
          <span className="text-fg-tertiary">Company claim: {org.company_claim} (cannot be changed)</span>
          <label className="flex flex-col gap-1 text-fg-secondary">
            Display name
            <input
              value={displayName}
              maxLength={256}
              placeholder={org.company_claim}
              onChange={(e) => setDisplayName(e.target.value)}
              className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
            />
          </label>
          <label className="flex flex-col gap-1 text-fg-secondary">
            SSO domains (comma-separated)
            <input
              value={domains}
              placeholder="example.se, example.com"
              onChange={(e) => setDomains(e.target.value)}
              className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
            />
            <span className="text-fg-tertiary">For reference only. This does not turn SSO on for a domain.</span>
          </label>
          <span role="radiogroup" aria-label="Status" className="grid gap-2">
            <span className="text-fg-secondary">Status</span>
            <label className="flex items-center gap-2 text-fg">
              <input type="radio" name={`status-${org.org_id}`} checked={orgStatus === 'active'} onChange={() => setOrgStatus('active')} />
              Active
            </label>
            <label className="flex items-center gap-2 text-fg">
              <input type="radio" name={`status-${org.org_id}`} checked={orgStatus === 'suspended'} onChange={() => setOrgStatus('suspended')} />
              Suspended
            </label>
          </span>
          {suspending && (
            <span className="text-danger">
              Suspending switches off every module, MCP included, for everyone in this organization
              within about 60 seconds. Their data is kept.
            </span>
          )}
        </span>
      )}
      error={error || undefined}
      confirmLabel="Save"
      variant={suspending ? 'destructive' : 'default'}
      submitting={submitting}
      onConfirm={save}
      onCancel={() => { if (!submitting) onCancel(); }}
    />
  );
}

export default function OrganizationsPanel({ authToken }: OrganizationsPanelProps) {
  const auth = useOptionalAuth();
  // auth_service allows usage terms for super_admins only and refuses the rest.
  const canEditUsage = (auth?.userData as any)?.role === 'super_admin';
  const [orgs, setOrgs] = useState<OrganizationSummary[]>([]);
  const [usageTermsOrg, setUsageTermsOrg] = useState<OrganizationSummary | null>(null);
  const [editOrg, setEditOrg] = useState<OrganizationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyOrg, setBusyOrg] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingToggle | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  // Create form. Collapsed by default — the table is the everyday view and
  // adding an organization happens once per customer.
  const [createOpen, setCreateOpen] = useState(false);
  const [newClaim, setNewClaim] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [newModules, setNewModules] = useState<Record<string, boolean>>({});
  const [accessMode, setAccessMode] = useState<'mcp-only' | 'web-mcp' | 'custom'>('custom');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [registry, setRegistry] = useState<ModuleRegistry | null>(null);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [registryError, setRegistryError] = useState<string | null>(null);
  // BAR-1528: free organizations are asked for only when this is on.
  const [showFree, setShowFree] = useState(false);
  // Only the first load replaces the panel with its loading line. A reload
  // from the toggle keeps the panel, and the toggle, where they are.
  const [hasLoaded, setHasLoaded] = useState(false);
  // BAR-1528: MCP usage per org, the plan switch, and one org's people.
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriod>('7d');
  const [usage, setUsage] = useState<Record<string, McpUsageOrg>>({});
  const [usageState, setUsageState] = useState<'loading' | 'ok' | 'unavailable' | 'off'>('loading');
  const [people, setPeople] = useState<PeopleView | null>(null);
  const [pendingPlan, setPendingPlan] = useState<PendingPlan | null>(null);
  // Only the newest usage load may write: a slow 30-day answer must not land
  // under the 7-day header after the period changed back.
  const usageSeq = useRef(0);

  const loadOrgs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.listOrganizations(authToken, { includeFree: showFree });
      setOrgs(Array.isArray(result?.organizations) ? result.organizations : []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to load organizations');
    } finally {
      setIsLoading(false);
    }
  }, [authToken, showFree]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  useEffect(() => {
    let active = true;
    setRegistry(null);
    setRegistryError(null);
    setRegistryLoading(true);
    api.getModuleRegistry(authToken)
      .then((result) => {
        if (active) setRegistry(result);
      })
      .catch((e: any) => {
        if (active) setRegistryError(e?.message || 'Failed to load module registry');
      })
      .finally(() => {
        if (active) setRegistryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authToken]);

  const loadUsage = useCallback(async () => {
    const seq = ++usageSeq.current;
    setUsageState('loading');
    try {
      if (!(await api.hasMcpUsage())) {
        if (seq === usageSeq.current) setUsageState('off');
        return;
      }
      const result = await api.getMcpUsageOrgs(authToken, usagePeriod);
      if (seq !== usageSeq.current) return;
      const byOrg: Record<string, McpUsageOrg> = {};
      for (const row of result?.orgs || []) byOrg[row.org_id] = row;
      setUsage(byOrg);
      setUsageState('ok');
    } catch (e: any) {
      if (seq !== usageSeq.current) return;
      // The dashboard serves verified BARK-domain staff only. Any other admin
      // gets 403 every time, and an impersonation token gets 401 every time, so
      // show no usage rather than a lasting failure. An expired token also gets
      // 401, but the refreshed token changes `authToken`, which runs this load
      // again.
      setUsageState(e?.status === 401 || e?.status === 403 ? 'off' : 'unavailable');
    }
  }, [authToken, usagePeriod]);

  useEffect(() => {
    loadUsage();
    // A period change makes an open people list stale.
    setPeople(null);
  }, [loadUsage]);

  const loadPeople = async (org: OrganizationSummary) => {
    const orgId = org.org_id;
    setPeople({ orgId, state: 'loading', rows: [], revealed: false });
    try {
      const result = await api.getMcpUsagePeople(authToken, orgId, usagePeriod);
      // Only if this org is still the one open.
      setPeople((prev) =>
        prev?.orgId === orgId
          ? {
              orgId,
              state: 'ok',
              rows: result?.people || [],
              revealed: !!result?.revealed,
            }
          : prev,
      );
    } catch {
      setPeople((prev) =>
        prev?.orgId === orgId ? { orgId, state: 'error', rows: [], revealed: false } : prev,
      );
    }
  };

  const togglePeople = (org: OrganizationSummary) => {
    if (people?.orgId === org.org_id) {
      setPeople(null);
      return;
    }
    loadPeople(org);
  };

  const requestPlan = (org: OrganizationSummary, next: 'free' | 'full') => {
    setDialogError(null);
    setPendingPlan({ org, next });
  };

  const confirmPlan = async () => {
    if (!pendingPlan) return;
    const { org, next } = pendingPlan;
    setSubmitting(true);
    setDialogError(null);
    setBusyOrg(org.org_id);
    try {
      const updated = await api.patchOrganization(authToken, org.org_id, { plan: next });
      // An auth service without the plan field ignores it and still answers
      // 200, so trust only the plan it sends back.
      if (updated?.plan !== next) {
        setDialogError('The auth service did not apply the plan change. Nothing changed.');
        return;
      }
      setOrgs((prev) => prev.map((o) => (o.org_id === org.org_id ? { ...o, ...updated } : o)));
      setToast({
        kind: 'success',
        text: `${org.display_name || org.company_claim} is now on the ${next} plan (applies within 60 seconds)`,
      });
      setPendingPlan(null);
    } catch (e: any) {
      setDialogError(e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
      setBusyOrg(null);
    }
  };


  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // The full module id set: union of every org's effective map, falling back to
  // the known vocabulary so the columns are stable even before data loads.
  const moduleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const org of orgs) {
      Object.keys(org.effective_modules || {}).forEach((k) => ids.add(k));
    }
    if (ids.size === 0) return KNOWN_MODULE_IDS;
    // Keep the known ordering first, then any extras.
    const ordered = KNOWN_MODULE_IDS.filter((k) => ids.has(k));
    const extras = Array.from(ids).filter((k) => !KNOWN_MODULE_IDS.includes(k)).sort();
    return [...ordered, ...extras];
  }, [orgs]);

  // Same order as the table's module columns, so a box sits under its column.
  const createModuleIds = useMemo(() => {
    if (!registry) return [];
    const ids = [...registry.web_modules, registry.mcp_access_module];
    const ordered = KNOWN_MODULE_IDS.filter((k) => ids.includes(k));
    const extras = ids.filter((k) => !KNOWN_MODULE_IDS.includes(k)).sort();
    return [...ordered, ...extras];
  }, [registry]);

  const showUsage = usageState !== 'off';
  const totalColumns = 4 + (showUsage ? 3 : 0) + moduleIds.length;

  const effectiveValue = (org: OrganizationSummary, moduleId: string): boolean => {
    const eff = org.effective_modules || {};
    if (moduleId in eff) return !!eff[moduleId];
    // No computed value — fall back to the stored override if present.
    return !!(org.modules || {})[moduleId];
  };

  const requestToggle = (org: OrganizationSummary, moduleId: string) => {
    setDialogError(null);
    setPending({ org, moduleId, next: !effectiveValue(org, moduleId) });
  };

  const confirmToggle = async () => {
    if (!pending) return;
    const { org, moduleId, next } = pending;
    setSubmitting(true);
    setDialogError(null);
    setBusyOrg(org.org_id);
    try {
      // Merge into the stored PARTIAL override map (absent key ⇒ registry
      // default) — keeps the override minimal rather than materialising every
      // module as an explicit override.
      const nextModules = { ...(org.modules || {}), [moduleId]: next };
      const updated = await api.patchOrganization(authToken, org.org_id, { modules: nextModules });
      setOrgs((prev) =>
        prev.map((o) => (o.org_id === org.org_id ? { ...o, ...updated } : o)),
      );
      setToast({
        kind: 'success',
        text: `${org.display_name || org.company_claim}: ${moduleLabel(moduleId)} ${next ? 'enabled' : 'disabled'} (applies within ~60s)`,
      });
      setPending(null);
    } catch (e: any) {
      setDialogError(e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
      setBusyOrg(null);
    }
  };

  const selectAccessMode = (mode: 'mcp-only' | 'web-mcp' | 'custom') => {
    setAccessMode(mode);
    if (!registry || mode === 'custom') return;
    if (mode === 'web-mcp') {
      setNewModules({ [registry.mcp_access_module]: true });
      return;
    }
    const modules: Record<string, boolean> = {};
    for (const id of registry.web_modules) modules[id] = false;
    modules[registry.mcp_access_module] = true;
    setNewModules(modules);
  };

  const toggleNewModule = (id: string, value: boolean) => {
    if (!registry) return;
    setAccessMode('custom');
    setNewModules((prev) => {
      const next = { ...prev };
      if (value !== (registry.defaults[id] ?? false)) next[id] = value;
      else delete next[id];
      return next;
    });
  };

  const resetCreateForm = () => {
    setNewClaim('');
    setNewDisplayName('');
    setNewDomains('');
    setNewModules({});
    setAccessMode('custom');
    setCreateError(null);
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const claim = newClaim.trim();
    if (!claim) {
      setCreateError('Company claim is required.');
      return;
    }
    if (!registry) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await api.createOrganization(authToken, {
        company_claim: claim,
        display_name: newDisplayName.trim() || undefined,
        // Descriptive only — this does NOT open SSO for the domain, which is
        // membership in SSO_DOMAIN_TO_COMPANY and a separate decision.
        sso_domains: newDomains
          .split(',')
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        // Send only what the admin actually switched: an absent key keeps its
        // registry default, and writing every module as an explicit value would
        // freeze this org against future registry changes.
        modules: newModules,
      });
      setOrgs((prev) => [...prev, created]);
      setToast({
        kind: 'success',
        text: `${created.display_name || created.company_claim} created (${created.org_id}) — applies within ~60s`,
      });
      resetCreateForm();
      setCreateOpen(false);
    } catch (err: any) {
      setCreateError(err?.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  if (isLoading && !hasLoaded) {
    return <p className="text-sm text-fg-secondary">Loading organizations…</p>;
  }
  if (error) {
    return (
      <div className="text-sm text-danger">
        <p>{error}</p>
        <button onClick={loadOrgs} className="mt-2 underline">Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 rounded-md border border-border-default bg-bg-warm px-4 py-2 text-[13px] text-fg-secondary">
        Module changes apply within <strong className="text-fg">~60 seconds</strong> — no redeploy or re-login needed.
      </div>

      {canEditUsage && <FreePlanAllowance authToken={authToken} onSaved={loadOrgs} />}

      {toast && (
        <div
          role="status"
          className={
            'mb-4 rounded-md border px-4 py-2 text-sm ' +
            (toast.kind === 'success'
              ? 'border-success text-success bg-success/10'
              : 'border-danger text-danger bg-danger/10')
          }
        >
          {toast.text}
        </div>
      )}

      <div className="mb-4">
        {createOpen ? (
          <form
            onSubmit={submitCreate}
            aria-label="New organization"
            className="rounded-md border border-border-default bg-surface p-4"
          >
            <h3 className="mb-1 font-body text-[15px] font-semibold text-fg">New organization</h3>
            <p className="mb-4 text-[13px] text-fg-secondary">
              The company claim must match what the organization's accounts already carry
              (e.g. <code>Norion Bank</code>, not <code>norionbank.se</code>). It is the join
              key and the first segment of their storage path, so a mismatch creates a tenant
              nobody resolves to.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-[13px] text-fg-secondary">
                Company claim
                <input
                  type="text"
                  required
                  value={newClaim}
                  onChange={(e) => setNewClaim(e.target.value)}
                  placeholder="Norion Bank"
                  className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-fg-secondary">
                Display name <span className="text-fg-tertiary">(optional)</span>
                <input
                  type="text"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  placeholder="Norion Bank"
                  className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-fg-secondary sm:col-span-2">
                Email domains <span className="text-fg-tertiary">(optional, comma-separated — descriptive, does not open SSO)</span>
                <input
                  type="text"
                  value={newDomains}
                  onChange={(e) => setNewDomains(e.target.value)}
                  placeholder="norionbank.se, norion.se"
                  className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus"
                />
              </label>
            </div>

            <fieldset className="mt-4">
              <legend className="mb-2 text-[13px] text-fg-secondary">
                Modules — ticked boxes are what this organization gets.
              </legend>
              {registryLoading && (
                <p role="status" className="text-[13px] text-fg-secondary">Loading module defaults…</p>
              )}
              {registryError && (
                <p role="alert" className="text-[13px] text-danger">{registryError}</p>
              )}
              {registry && (
                <>
                  <div className="mb-4" role="radiogroup" aria-labelledby="create-access-label">
                    <div id="create-access-label" className="mb-2 text-[13px] text-fg-secondary">Access</div>
                    <div className="grid gap-2">
                      <label className="flex items-start gap-2 text-[13px] text-fg">
                        <input
                          type="radio"
                          name="organization-access"
                          value="mcp-only"
                          checked={accessMode === 'mcp-only'}
                          onChange={() => selectAccessMode('mcp-only')}
                        />
                        <span>
                          <span className="block">MCP only</span>
                          <span className="block text-fg-secondary">Works in Claude, ChatGPT and Codex. The web app stays closed.</span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 text-[13px] text-fg">
                        <input
                          type="radio"
                          name="organization-access"
                          value="web-mcp"
                          checked={accessMode === 'web-mcp'}
                          onChange={() => selectAccessMode('web-mcp')}
                        />
                        <span>
                          <span className="block">Web app + MCP</span>
                          <span className="block text-fg-secondary">The default web modules plus BARK over MCP.</span>
                        </span>
                      </label>
                      <label className="flex items-center gap-2 text-[13px] text-fg">
                        <input
                          type="radio"
                          name="organization-access"
                          value="custom"
                          checked={accessMode === 'custom'}
                          onChange={() => selectAccessMode('custom')}
                        />
                        Custom
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {createModuleIds.map((id) => (
                      <label key={id} className="flex items-center gap-2 text-[13px] text-fg">
                        <input
                          type="checkbox"
                          checked={newModules[id] ?? registry.defaults[id] ?? false}
                          onChange={(e) => toggleNewModule(id, e.target.checked)}
                        />
                        {moduleLabel(id)}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </fieldset>

            {createError && (
              <p role="alert" className="mt-3 text-sm text-danger">{createError}</p>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={creating || !registry}
                className="rounded-md bg-[color:var(--primary)] px-4 py-2 text-sm font-semibold text-on-primary disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create organization'}
              </button>
              <button
                type="button"
                disabled={creating}
                onClick={() => {
                  resetCreateForm();
                  setCreateOpen(false);
                }}
                className="rounded-md border border-border-default px-4 py-2 text-sm text-fg-secondary disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-md border border-border-default bg-surface px-4 py-2 text-sm font-semibold text-fg hover:bg-bg-warm"
          >
            New organization
          </button>
        )}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <label className="flex items-center gap-2 text-[13px] text-fg">
          <input
            type="checkbox"
            checked={showFree}
            disabled={isLoading}
            onChange={(e) => setShowFree(e.target.checked)}
          />
          Show free organizations
        </label>
        {showUsage && (
          <label className="flex items-center gap-2 text-[13px] text-fg">
            MCP usage
            <select
              value={usagePeriod}
              onChange={(e) => setUsagePeriod(e.target.value as UsagePeriod)}
              className="min-h-8 rounded-md border border-border-default bg-surface px-2 py-1 text-[13px] text-fg focus:outline-none focus-visible:shadow-bark-focus"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </label>
        )}
        {usageState === 'loading' && (
          <span role="status" className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
            Loading MCP usage…
          </span>
        )}
        {usageState === 'unavailable' && (
          <span role="alert" className="flex items-center gap-2 text-[13px] text-danger">
            MCP usage is unavailable right now.
            <button type="button" onClick={loadUsage} className="inline-flex min-h-8 items-center px-1 underline">
              Retry
            </button>
          </span>
        )}
        {isLoading && (
          <span role="status" className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
            Loading organizations…
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-border-default">
        <table className="min-w-full divide-y divide-border-default">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Organization</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Users</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap">Usage</th>
              {showUsage && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap">
                    MCP calls ({PERIOD_LABELS[usagePeriod]})
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap">Limit refusals</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap">Last MCP call</th>
                </>
              )}
              {moduleIds.map((id) => (
                <th
                  key={id}
                  className="px-4 py-3 text-center text-xs font-semibold text-fg-secondary uppercase tracking-wider whitespace-nowrap"
                >
                  {moduleLabel(id)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border-default">
            {orgs.map((org) => {
              const busy = busyOrg === org.org_id;
              const label = org.display_name || org.company_claim;
              const orgUsage = usage[org.org_id];
              const peopleOpen = people?.orgId === org.org_id;
              return (
                <React.Fragment key={org.org_id}>
                <tr>
                  <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                    <div className="font-medium">
                      {label}
                      {isMcpOnlyModuleMap(org.effective_modules) && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-bg-warm text-fg-secondary">
                          MCP only
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-fg-tertiary">
                      {org.company_claim}
                      {org.status === 'suspended' && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-danger/10 text-danger">
                          suspended
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setEditOrg(org)}
                      className="mt-1 mr-3 inline-flex min-h-8 items-center text-xs text-fg-secondary underline disabled:opacity-50 focus:outline-none focus-visible:shadow-bark-focus"
                    >
                      Edit
                    </button>
                    {usageState === 'ok' && (
                      <button
                        type="button"
                        aria-expanded={peopleOpen}
                        aria-controls={`mcp-people-${org.org_id}`}
                        onClick={() => togglePeople(org)}
                        className="mt-1 inline-flex min-h-8 items-center text-xs text-fg-secondary underline focus:outline-none focus-visible:shadow-bark-focus"
                      >
                        {peopleOpen ? 'Hide people' : 'People'}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-secondary whitespace-nowrap">
                    {typeof org.user_count === 'number' ? org.user_count : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span
                      className={
                        'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] ' +
                        (planOf(org) === 'free'
                          ? 'bg-warning-bg text-warning-ink'
                          : 'bg-bg-warm text-fg-secondary')
                      }
                    >
                      {planOf(org) === 'free' ? 'Free' : 'Full'}
                    </span>
                    {planOf(org) === 'free' ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => requestPlan(org, 'full')}
                        className="ml-2 rounded-md border border-border-default px-3 py-2 text-xs font-semibold text-fg hover:bg-bg-warm disabled:opacity-50 focus:outline-none focus-visible:shadow-bark-focus"
                      >
                        Give full access
                      </button>
                    ) : inFreeNamespace(org.org_id) ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => requestPlan(org, 'free')}
                        className="ml-2 rounded-md border border-border-default px-3 py-2 text-xs text-fg-secondary hover:bg-bg-warm disabled:opacity-50 focus:outline-none focus-visible:shadow-bark-focus"
                      >
                        Back to free
                      </button>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                    {org.usage ? (
                      <>
                        <div>
                          {compactTokens(org.usage.used_tokens)} of{' '}
                          {org.usage.allowance_tokens == null ? 'Unlimited' : `${compactTokens(org.usage.allowance_tokens)} tokens`}
                          {org.usage.allowance_source === 'plan_default' && (
                            <span className="text-fg-secondary"> · default</span>
                          )}
                        </div>
                        <div className="text-xs text-fg-secondary">
                          {formatUsd(org.usage.billed_usd)} billed · {formatNumber(org.usage.markup_pct)}% markup
                        </div>
                      </>
                    ) : '—'}
                    {canEditUsage && (
                      <button
                        type="button"
                        onClick={() => setUsageTermsOrg(org)}
                        className="mt-1 inline-flex min-h-8 items-center text-xs text-fg-secondary underline focus:outline-none focus-visible:shadow-bark-focus"
                      >
                        Usage terms
                      </button>
                    )}
                  </td>
                  {showUsage && (
                    <>
                      <td className="px-4 py-3 text-right text-sm text-fg whitespace-nowrap">
                        {usageState === 'ok' ? (orgUsage?.calls ?? 0) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-fg-secondary whitespace-nowrap">
                        {usageState === 'ok' ? (orgUsage?.limit_refusals ?? 0) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-fg-secondary whitespace-nowrap">
                        {usageState === 'ok' ? formatWhen(orgUsage?.last_call) : '—'}
                      </td>
                    </>
                  )}
                  {moduleIds.map((id) => {
                    const on = effectiveValue(org, id);
                    const isOverride = id in (org.modules || {});
                    return (
                      <td key={id} className="px-4 py-3 text-center">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={on}
                          aria-label={`${moduleLabel(id)} for ${org.display_name || org.company_claim}: ${on ? 'enabled' : 'disabled'}`}
                          disabled={busy}
                          onClick={() => requestToggle(org, id)}
                          title={isOverride ? 'Explicit override' : 'Registry default'}
                          className={
                            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:shadow-bark-focus disabled:opacity-50 ' +
                            (on ? 'bg-[color:var(--primary)]' : 'bg-surface-sunken border border-border-default')
                          }
                        >
                          <span
                            className={
                              'inline-block h-4 w-4 transform rounded-full bg-surface shadow-bark-xs transition-transform ' +
                              (on ? 'translate-x-4' : 'translate-x-0.5')
                            }
                          />
                        </button>
                      </td>
                    );
                  })}
                </tr>
                {peopleOpen && people && (
                  <tr id={`mcp-people-${org.org_id}`}>
                    <td colSpan={totalColumns} className="bg-bg-warm px-4 py-3">
                      {people.state === 'loading' && (
                        <p role="status" className="text-[13px] text-fg-secondary">Loading people…</p>
                      )}
                      {people.state === 'error' && (
                        <p role="alert" className="flex items-center gap-2 text-[13px] text-danger">
                          Could not load the people of {label}.
                          <button type="button" onClick={() => loadPeople(org)} className="inline-flex min-h-8 items-center px-1 underline">
                            Retry
                          </button>
                        </p>
                      )}
                      {people.state === 'ok' && people.rows.length === 0 && (
                        <p className="text-[13px] text-fg-secondary">No MCP signups or calls for {label} in the last {PERIOD_LABELS[usagePeriod]}.</p>
                      )}
                      {people.state === 'ok' && people.rows.length > 0 && (
                        <>
                          <table aria-label={`People in ${label}`} className="min-w-full text-[13px]">
                            <thead>
                              <tr className="text-left text-xs text-fg-secondary">
                                <th className="py-1 pr-4 font-semibold">Person</th>
                                <th className="py-1 pr-4 font-semibold">Role</th>
                                <th className="py-1 pr-4 font-semibold">Signed up</th>
                                <th className="py-1 pr-4 font-semibold">Activated</th>
                                <th className="py-1 pr-4 font-semibold">Clients</th>
                                <th className="py-1 pr-4 text-right font-semibold">MCP calls</th>
                                <th className="py-1 pr-4 text-right font-semibold">Limit refusals</th>
                                <th className="py-1 font-semibold">Last call</th>
                              </tr>
                            </thead>
                            <tbody>
                              {people.rows.map((person) => (
                                <tr key={person.person} className="text-fg">
                                  <td className="py-1 pr-4">{person.email || person.person}</td>
                                  <td className="py-1 pr-4">{person.role || '—'}</td>
                                  <td className="py-1 pr-4">{formatDay(person.signed_up_at)}</td>
                                  <td className="py-1 pr-4">{formatDay(person.activated_at)}</td>
                                  <td className="py-1 pr-4">{formatMcpClients(person.clients)}</td>
                                  <td className="py-1 pr-4 text-right">{formatNumber(person.calls)}</td>
                                  <td className="py-1 pr-4 text-right">{formatNumber(person.limit_refusals)}</td>
                                  <td className="py-1">{formatWhen(person.last_call)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {!people.revealed && (
                            <p className="mt-2 text-xs text-fg-tertiary">
                              Emails show only to the analytics dashboard's identity-reveal list.
                            </p>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {orgs.length === 0 && (
              <tr>
                <td colSpan={totalColumns} className="px-4 py-6 text-center text-sm text-fg-secondary">
                  No organizations yet. Every company resolves to the registry
                  defaults until one exists, which means no Build for anyone —
                  use “New organization” above to grant it.
                  {!showFree && ' Free organizations are not listed. Turn on “Show free organizations” to see them.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending
            ? `${pending.next ? 'Enable' : 'Disable'} ${moduleLabel(pending.moduleId)}?`
            : ''
        }
        description={
          pending ? (
            <>
              {pending.next ? 'Enable' : 'Disable'}{' '}
              <strong>{moduleLabel(pending.moduleId)}</strong> for{' '}
              <strong>{pending.org.display_name || pending.org.company_claim}</strong>. This is a
              billing-tied entitlement and applies to every user in the organization within
              ~60 seconds.
            </>
          ) : undefined
        }
        error={dialogError || undefined}
        confirmLabel={pending?.next ? 'Enable' : 'Disable'}
        variant={pending && !pending.next ? 'destructive' : 'default'}
        submitting={submitting}
        onConfirm={confirmToggle}
        onCancel={() => {
          if (submitting) return;
          setPending(null);
          setDialogError(null);
        }}
      />

      <ConfirmDialog
        open={pendingPlan !== null}
        title={
          pendingPlan
            ? pendingPlan.next === 'full'
              ? `Give ${pendingPlan.org.display_name || pendingPlan.org.company_claim} full access?`
              : `Move ${pendingPlan.org.display_name || pendingPlan.org.company_claim} back to the free plan?`
            : ''
        }
        description={
          pendingPlan ? (
            pendingPlan.next === 'full' ? (
              <>
                The free default allowance stops applying within 60 seconds: usage is unlimited
                unless an allowance is set for the organization. Web modules stay off; switch them
                on in this table. The change is recorded in the audit log under your name.
              </>
            ) : (
              <>
                The free plan allowance applies again within 60 seconds unless an
                allowance is set for the organization. The change is recorded in the audit log
                under your name.
              </>
            )
          ) : undefined
        }
        error={dialogError || undefined}
        confirmLabel={pendingPlan?.next === 'full' ? 'Give full access' : 'Back to free'}
        variant={pendingPlan?.next === 'free' ? 'destructive' : 'default'}
        submitting={submitting}
        onConfirm={confirmPlan}
        onCancel={() => {
          if (submitting) return;
          setPendingPlan(null);
          setDialogError(null);
        }}
      />

      {editOrg && (
        <EditOrganizationDialog
          authToken={authToken}
          org={editOrg}
          onCancel={() => setEditOrg(null)}
          onSaved={(updated) => {
            setOrgs((prev) => prev.map((org) => (
              org.org_id === editOrg.org_id ? { ...org, ...updated } : org
            )));
            setToast({ kind: 'success', text: `${updated.display_name || updated.company_claim} saved (applies within ~60s)` });
            setEditOrg(null);
          }}
        />
      )}

      {usageTermsOrg && (
        <UsageTermsDialog
          authToken={authToken}
          org={usageTermsOrg}
          onCancel={() => setUsageTermsOrg(null)}
          onSaved={(updated) => {
            setOrgs((prev) => prev.map((org) => (
              org.org_id === usageTermsOrg.org_id ? { ...org, ...updated } : org
            )));
            setToast({ kind: 'success', text: 'Usage terms saved (applies within 60 seconds)' });
            setUsageTermsOrg(null);
          }}
        />
      )}
    </>
  );
}
